import React, { useMemo, useState } from 'react';
import { isDesktop, isMobile } from 'react-device-detect';
import { useGetIdentity, useMenu } from '@refinedev/core';
import type { IERPUser } from '@admin-panel/types';
import { WarehouseFacilitySelector, VendorSelector } from '../header';
import { BiohazardIcon, DotsThreeVerticalIcon } from '@phosphor-icons/react';
import { PORTAL_NAME } from '@admin-panel/config/env';
import { Drawer, Grid, Space, Spin, Typography } from 'antd';
import Sider from '../Sider';
import * as Styles from './styles';
import { PageHeaderProvider, usePageHeaderState } from './page-header-context';

type LayoutProps = {
  children: React.ReactNode;
};

const isLocalConnectedToProduction =
  import.meta.env['VITE_APP_ENV'] === 'local' &&
  import.meta.env['VITE_GATEWAY_API_URL'] === 'https://gateway.lyskraft.in/api/admin';

const Layout = ({ children }: LayoutProps) => {
  const { menuItems, selectedKey } = useMenu();
  const { data: user } = useGetIdentity<IERPUser>();
  const screens = Grid.useBreakpoint();

  const currentMenuItem = useMemo(() => {
    const traverse = (items: any[]): any | null => {
      for (const item of items) {
        if (item.key === selectedKey) return item;
        if (item.children) {
          const found = traverse(item.children);
          if (found) return found;
        }
      }
      return null;
    };
    return traverse(menuItems);
  }, [menuItems, selectedKey]);

  const pageTitle = currentMenuItem?.label || currentMenuItem?.name || 'Dashboard';

  return (
    <PageHeaderProvider>
      {isLocalConnectedToProduction && <EnvironmentalHazard />}
      <Styles.LayoutRoot>
        <Sider />
        <Styles.PageArea $isMobile={isMobile}>
          {isDesktop && <HeaderBar defaultTitle={pageTitle} user={user} isDesktop={!!screens.md} />}

          <Styles.ContentWrapper $isMobile={isMobile}>
            {!isDesktop && <MobileActions />}
            {children}
          </Styles.ContentWrapper>
        </Styles.PageArea>
      </Styles.LayoutRoot>
      {/* <Toaster /> */}
    </PageHeaderProvider>
  );
};

const TOP_ACTIONS_LIMIT = 4;

type HeaderBarProps = {
  defaultTitle: string;
  user?: IERPUser;
  isDesktop: boolean;
};

const HeaderBar = ({ defaultTitle, user, isDesktop }: HeaderBarProps) => {
  const { title, actions, isLoading } = usePageHeaderState();
  const effectiveTitle = title ?? defaultTitle;
  const hasPortalSelectors = PORTAL_NAME === 'warehouse' || PORTAL_NAME === 'vendor';
  const computeActions = () => {
    if (!actions) {
      return {
        topActions: null as React.ReactNode,
        bottomActions: null as React.ReactNode,
      };
    }

    if (hasPortalSelectors) {
      return {
        topActions: null,
        bottomActions: actions,
      };
    }

    const nodesArray = React.Children.toArray(actions);

    if (nodesArray.length === 1) {
      const singleNode = nodesArray[0];

      if (React.isValidElement(singleNode) && singleNode.props?.children) {
        const childArray = React.Children.toArray(singleNode.props.children);

        if (childArray.length > TOP_ACTIONS_LIMIT) {
          const topChildNodes = childArray.slice(0, TOP_ACTIONS_LIMIT);
          const bottomChildNodes = childArray.slice(TOP_ACTIONS_LIMIT);

          const topElement = React.cloneElement(
            singleNode,
            {
              key: singleNode.key ?? 'top-actions',
            },
            topChildNodes,
          );
          const bottomElement = React.cloneElement(
            singleNode,
            {
              key: singleNode.key ? `${singleNode.key}-overflow` : 'bottom-actions',
            },
            bottomChildNodes,
          );

          return {
            topActions: topElement,
            bottomActions: bottomElement,
          };
        }
      }
    }

    if (nodesArray.length <= TOP_ACTIONS_LIMIT) {
      return {
        topActions: nodesArray,
        bottomActions: null,
      };
    }

    return {
      topActions: nodesArray.slice(0, TOP_ACTIONS_LIMIT),
      bottomActions: nodesArray.slice(TOP_ACTIONS_LIMIT),
    };
  };

  const { topActions, bottomActions } = computeActions();

  return (
    <>
      <Styles.HeaderBar $isMobile={isMobile}>
        <Styles.Top $isMobile={isMobile}>
          <Typography.Title
            level={3}
            style={{
              margin: 0,
              textTransform: 'uppercase',
              letterSpacing: '2px',
              fontWeight: 700,
              width: 'fit-content',
            }}
            className="font-gloock"
          >
            {effectiveTitle}
          </Typography.Title>
          <Space
            size={12}
            align="center"
            style={{ justifyContent: 'flex-end', flex: 1, flexWrap: 'wrap' }}
            data-testid="top-page-actions"
          >
            {isLoading && <Spin size="small" />}
            {PORTAL_NAME === 'warehouse' && <WarehouseFacilitySelector user={user} />}
            {PORTAL_NAME === 'vendor' && <VendorSelector user={user} />}
            {topActions}
          </Space>
        </Styles.Top>
      </Styles.HeaderBar>
      {bottomActions && (
        <Styles.Bottom data-testid="bottom-page-actions">{bottomActions}</Styles.Bottom>
      )}
    </>
  );
};

const MobileActions = () => {
  const { actions, isLoading } = usePageHeaderState();
  const [actionsOpen, setActionsOpen] = useState(false);

  if (!isMobile) return null;
  const shouldRender = Boolean(actions) || isLoading;
  if (!shouldRender) return null;

  return (
    <Styles.MobileActionsBar>
      {!actions && isLoading && (
        <Styles.MobileActionsLoading>
          <Spin size="small" />
        </Styles.MobileActionsLoading>
      )}
      {actions && (
        <>
          <Styles.MobileActionsTrigger
            size="large"
            loading={isLoading}
            icon={<DotsThreeVerticalIcon size={18} weight="bold" />}
            onClick={() => setActionsOpen(true)}
            aria-label="Open page actions"
          >
            Actions
          </Styles.MobileActionsTrigger>
          <Drawer
            title={
              <Styles.MobileActionsSheetTitle>
                <span>Page actions</span>
                <small>Choose the action you need for this page</small>
              </Styles.MobileActionsSheetTitle>
            }
            placement="bottom"
            open={actionsOpen}
            onClose={() => setActionsOpen(false)}
            height="auto"
            styles={{
              mask: { background: 'rgba(15, 23, 42, 0.24)' },
              content: {
                borderTopLeftRadius: 20,
                borderTopRightRadius: 20,
                maxHeight: '82dvh',
                overflow: 'hidden',
              },
              header: { padding: '18px 20px 12px', borderBottom: 'none' },
              body: {
                padding: '4px 20px 20px',
                maxHeight: 'calc(82dvh - 76px)',
                overflowY: 'auto',
              },
            }}
          >
            <Styles.MobileActionsSheetHandle aria-hidden="true" />
            <Styles.MobileActionsContent>{actions}</Styles.MobileActionsContent>
          </Drawer>
        </>
      )}
    </Styles.MobileActionsBar>
  );
};

const EnvironmentalHazard = () => {
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        zIndex: 9999,
        width: '100%',
        background: '#FF0000',
        height: '2px',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
      }}
    >
      <Typography.Text
        style={{
          background: '#FF0000',
          color: '#ffffff',
          fontSize: '10px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '0 6px',
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        <BiohazardIcon>
          <animateTransform
            attributeName="transform"
            attributeType="XML"
            type="rotate"
            dur="3s"
            from="0 0 0"
            to="360 0 0"
            repeatCount="indefinite"
          ></animateTransform>
        </BiohazardIcon>
        localhost is connected to production
      </Typography.Text>
    </div>
  );
};

export default Layout;
